/**
 * Bookings API + TanStack Query hooks.
 *
 * Mirrors @features/attendance/api/attendance.ts and
 * @features/calendar/api/sessions.ts:
 *   - typed via the generated OpenAPI schema (`@api/index`)
 *   - centralised query-key factory
 *   - mutations invalidate the list cache AND the catalog entitlements
 *     cache (creating/cancelling a booking shifts a session counter)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@core/api/client';
import type { components } from '@api/index';

export type BookingResponse = components['schemas']['BookingResponse'];
export type BookingListResponse = components['schemas']['BookingListResponse'];
export type BookingCreate = components['schemas']['BookingCreate'];
export type BookingUpdate = components['schemas']['BookingUpdate'];
export type BookingCancel = components['schemas']['BookingCancel'];
export type BookingStatus = BookingResponse['status'];
export type BookingType = BookingResponse['booking_type'];
export type BookingSource = BookingResponse['source'];
export type ExternalContact = NonNullable<BookingResponse['external_contact']>;

export interface BookingsFilters {
  studentId?: string;
  sessionId?: string;
  status?: BookingStatus | '';
  bookingType?: BookingType | '';
  from?: string;
  to?: string;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const bookingsKeys = {
  all: ['bookings'] as const,
  list: (filters: BookingsFilters) =>
    ['bookings', 'list', filters] as const,
  detail: (id: string) => ['bookings', 'detail', id] as const,
};

// ---------------------------------------------------------------------------
// REST callers
// ---------------------------------------------------------------------------

function toQuery(filters: BookingsFilters): Record<string, string | number | undefined> {
  return {
    student_id: filters.studentId || undefined,
    session_id: filters.sessionId || undefined,
    status: filters.status || undefined,
    booking_type: filters.bookingType || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    limit: filters.limit ?? undefined,
  };
}

export async function listBookings(
  filters: BookingsFilters,
): Promise<BookingListResponse> {
  return api.get<BookingListResponse>('/v1/bookings', { query: toQuery(filters) });
}

export async function getBooking(id: string): Promise<BookingResponse> {
  return api.get<BookingResponse>(`/v1/bookings/${id}`);
}

export async function createBooking(
  body: BookingCreate,
  idempotencyKey?: string,
): Promise<BookingResponse> {
  return api.post<BookingResponse>('/v1/bookings', body, {
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

export async function updateBooking(
  id: string,
  body: BookingUpdate,
): Promise<BookingResponse> {
  return api.patch<BookingResponse>(`/v1/bookings/${id}`, body);
}

export async function cancelBooking(
  id: string,
  body: BookingCancel,
): Promise<BookingResponse> {
  return api.post<BookingResponse>(`/v1/bookings/${id}/cancel`, body);
}

export async function confirmBooking(id: string): Promise<BookingResponse> {
  return api.post<BookingResponse>(`/v1/bookings/${id}/confirm`);
}

export async function markNoShow(id: string): Promise<BookingResponse> {
  return api.post<BookingResponse>(`/v1/bookings/${id}/no-show`);
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useBookings(filters: BookingsFilters = {}) {
  return useQuery({
    queryKey: bookingsKeys.list(filters),
    queryFn: () => listBookings(filters),
    staleTime: 30_000,
  });
}

export function useBooking(id: string | undefined) {
  return useQuery({
    queryKey: bookingsKeys.detail(id ?? '__none__'),
    queryFn: () => getBooking(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

function useInvalidators() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: bookingsKeys.all });
    // Booking flows shift entitlement counters → refresh the student
    // entitlements panel if one is open.
    qc.invalidateQueries({ queryKey: ['catalog', 'entitlements'] });
    // session.student_ids changed → invalidate any open attendance roster.
    qc.invalidateQueries({ queryKey: ['attendance'] });
    // 11E.7: the same write also moved the session's booked count, which
    // the calendar renders as `Subject (booked/capacity)`. Without this the
    // chip keeps the pre-enrolment number until the 15s staleTime lapses —
    // the enrol-then-look-at-calendar path is the common one, so it was
    // visibly wrong most of the time.
    qc.invalidateQueries({ queryKey: ['calendar', 'sessions'] });
  };
}

export function useCreateBooking() {
  const invalidate = useInvalidators();
  return useMutation({
    // Generate one idempotency key per submit attempt; client-side React
    // Query retries (and operator double-clicks) land on the same booking
    // doc instead of consuming two entitlements. ``crypto.randomUUID`` is
    // available in all evergreen browsers and Node 20+.
    mutationFn: (body: BookingCreate) =>
      createBooking(body, typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`),
    onSuccess: invalidate,
  });
}

export function useUpdateBooking() {
  const invalidate = useInvalidators();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: BookingUpdate }) =>
      updateBooking(id, body),
    onSuccess: invalidate,
  });
}

export function useCancelBooking() {
  const invalidate = useInvalidators();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: BookingCancel }) =>
      cancelBooking(id, body),
    onSuccess: invalidate,
  });
}

export function useConfirmBooking() {
  const invalidate = useInvalidators();
  return useMutation({
    mutationFn: confirmBooking,
    onSuccess: invalidate,
  });
}

export function useMarkNoShow() {
  const invalidate = useInvalidators();
  return useMutation({
    mutationFn: markNoShow,
    onSuccess: invalidate,
  });
}

